/**
 * El formulario de "Nuevo registro manual", como datos.
 *
 * Lo usan dos pantallas: Accesos (el vigilante registra una entrada o una
 * salida a mano) e Inspecciones (paso 1 de una pipa sin tag: decir qué unidad
 * entró con el tag compartido). Los campos son los mismos y las reglas
 * también, así que viven acá y no en ninguna de las dos.
 *
 * Módulo puro: las dos pantallas importan `utils/api` → firebase y no colectan
 * en un worktree sin `apps/v5/.env`.
 */

export interface ManualRegistrationFormData {
  type: "entry" | "exit";
  yard_id: string;
  // Optional transporter group used only to narrow the vehicle picker.
  // Not persisted — it's a client-side filter convenience.
  group_id: string;
  // Selected vehicle from the catalog. Sent as `vehicle_id`, which the backend
  // prefers when pairing the companion inspection (assignment history).
  vehicle_id: string;
  vehicle_plate: string;
  driver_name: string;
  card_number: string;
  notes: string;
}

export function emptyManualRegistrationForm(
  over: Partial<ManualRegistrationFormData> = {},
): ManualRegistrationFormData {
  return {
    type: "entry",
    yard_id: "",
    group_id: "",
    vehicle_id: "",
    vehicle_plate: "",
    driver_name: "",
    card_number: "",
    notes: "",
    ...over,
  };
}

/**
 * For ENTRY records the unit must be identified: either a catalog vehicle is
 * picked, or a plate was typed manually. Exits keep the looser behavior.
 */
export function isUnitMissing(
  form: Pick<ManualRegistrationFormData, "type" | "vehicle_id" | "vehicle_plate">,
): boolean {
  return form.type === "entry" && !form.vehicle_id && !form.vehicle_plate.trim();
}

export type ManualRegistrationErrorCode = "yardRequired" | "unitRequired";
export type ManualRegistrationErrors = Partial<
  Record<keyof ManualRegistrationFormData, ManualRegistrationErrorCode>
>;

/**
 * Códigos y no textos: quien pinta el formulario los traduce. El error de la
 * unidad se cuelga de `vehicle_plate` porque es el campo que el operador tiene
 * que llenar cuando el catálogo no trae la unidad.
 */
export function validateManualRegistration(
  form: ManualRegistrationFormData,
): ManualRegistrationErrors {
  const errors: ManualRegistrationErrors = {};
  if (!form.yard_id) errors.yard_id = "yardRequired";
  if (isUnitMissing(form)) errors.vehicle_plate = "unitRequired";
  return errors;
}

interface GroupLike {
  id: string;
  vehicle_ids?: string[];
}

interface VehicleLike {
  id: string;
  plate?: string;
}

/**
 * Los vehículos que ofrece el selector: con un grupo elegido, sólo los de ese
 * grupo (`vehicle_ids` lo resuelve el backend desde los dispositivos); sin
 * grupo, todos. Un grupo que ya no existe deja la lista vacía en vez de
 * abrirla entera — el filtro sigue puesto a la vista.
 */
export function vehiclesForGroup<V extends VehicleLike>(
  vehicles: V[],
  groups: readonly GroupLike[],
  groupId: string,
): V[] {
  if (!groupId) return vehicles;
  const ids = new Set(groups.find((g) => g.id === groupId)?.vehicle_ids || []);
  return vehicles.filter((v) => ids.has(v.id));
}

/**
 * La placa que viaja. Con un vehículo del catálogo se manda la SUYA para que
 * la inspección compañera quede con placa además de `vehicle_id`; lo escrito a
 * mano sólo sirve de respaldo si el catálogo no la tiene.
 */
export function resolveRegistrationPlate(
  form: Pick<ManualRegistrationFormData, "vehicle_id" | "vehicle_plate">,
  vehicles: readonly VehicleLike[],
): string {
  if (!form.vehicle_id) return form.vehicle_plate;
  return (
    vehicles.find((v) => v.id === form.vehicle_id)?.plate || form.vehicle_plate
  );
}

export interface InspectionRegistrationPayload {
  vehicle_id: string | null;
  vehicle_plate: string | null;
  driver_name: string | null;
  notes?: string | null;
}

/**
 * El body de `PUT /yardInspection/registration`.
 *
 * `notes` es la única clave condicional, y el motivo es el evento de entrada:
 * la nota vive ALLÁ (el contrato dice que la del registro no pisa las notas de
 * la inspección), así que al volver al paso 1 a corregir la placa el
 * formulario sólo la conoce si pudo leer ese evento. Si no pudo y el operador
 * no escribió nada, mandar `notes: null` borraría una nota que nunca vio; se
 * omite la clave y el backend deja la que había.
 */
export function inspectionRegistrationPayload(
  form: ManualRegistrationFormData,
  vehicles: readonly VehicleLike[],
  opts: { notesKnown: boolean },
): InspectionRegistrationPayload {
  const plate = resolveRegistrationPlate(form, vehicles).trim();
  const notes = form.notes.trim();
  const payload: InspectionRegistrationPayload = {
    vehicle_id: form.vehicle_id || null,
    vehicle_plate: plate || null,
    driver_name: form.driver_name.trim() || null,
  };
  if (opts.notesKnown || notes) payload.notes = notes || null;
  return payload;
}

/**
 * El formulario del paso 1 al abrirlo sobre una inspección: vacío la primera
 * vez, y con lo ya capturado cuando se vuelve a corregir. La placa escrita a
 * mano sólo se repone si NO hay vehículo del catálogo: con vehículo, el campo
 * de placa está oculto y la placa sale del catálogo.
 */
export function registrationFormFromInspection(inspection: {
  yard_id: string;
  vehicle_id?: string | null;
  vehicle_plate?: string | null;
  driver_name?: string | null;
  card_number?: string | null;
}): ManualRegistrationFormData {
  const vehicleId = inspection.vehicle_id || "";
  return emptyManualRegistrationForm({
    type: "entry",
    yard_id: inspection.yard_id,
    vehicle_id: vehicleId,
    vehicle_plate: vehicleId ? "" : inspection.vehicle_plate || "",
    driver_name: inspection.driver_name || "",
    card_number: inspection.card_number || "",
  });
}
